import type { PermissionContext } from '@ardoq/access-control';
import { actionCreator } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import { NodeModel } from '../models/types';
import { Position } from '../types';
import {
  APIComponentAttributes,
  APIModelAttributes,
  ArdoqId,
  ReferenceDirection,
} from '@ardoq/api-types';

export const clearHighlight = actionCreator(
  '[navigatorActions] CLEAR_HIGHLIGHT'
);

export type ContainerCreatedPayload = {
  readonly container: HTMLElement;
};
export const containerCreated = actionCreator<ContainerCreatedPayload>(
  '[navigatorActions] CONTAINER_CREATED'
);

export const containerHeightChanged = actionCreator(
  '[navigatorActions] CONTAINER_HEIGHT_CHANGED'
);

// TODO: This action is not used in the codebase
export const clearTree = actionCreator('[navigatorActions] CLEAR_TREE');

export const containerScrolled = actionCreator(
  '[navigatorActions] CONTAINER_SCROLLED'
);
// TODO where is this used?
export const resetScroll = actionCreator('[navigatorActions] RESET_SCROLL');

export const dragEndTransitionEnd = actionCreator(
  '[navigatorActions] DRAG_END_TRANSITION_END'
);

export type DragEnd = {
  readonly event: MouseEvent | KeyboardEvent;
  isAborted: boolean;
};
export const dragEnd = actionCreator<DragEnd>('[navigatorActions] DRAG_END');

export type FinalizeDragEndPayload = {
  readonly isAborted: boolean;
  readonly isHandled: boolean;
  readonly position: Position;
  readonly changedIds: string[];
};

export const finalizeDragEnd = actionCreator<FinalizeDragEndPayload>(
  '[navigatorActions] FINALIZE_DRAG_END'
);

export type ComponentsDroppedProcessedPayload = {
  changedComponents: Record<ArdoqId, APIComponentAttributes>;
  changedModels: Record<ArdoqId, APIModelAttributes>;
  locale: Locale;
};

export const componentsDroppedProcessed =
  actionCreator<ComponentsDroppedProcessedPayload>(
    '[navigatorActions] COMPONENTS_MOVED_BY_DND'
  );

export type DragStart = {
  readonly dragTargetNode: NodeModel;
  readonly dragStartPosition: Position;
};

export const dragStart = actionCreator<DragStart>(
  '[navigatorActions] DRAG_START'
);

export type DragUpdate = {
  readonly event: MouseEvent;
};
export const dragUpdate = actionCreator<DragUpdate>(
  '[navigatorActions] DRAG_UPDATE'
);

export type LinkUpdatePayload = {
  readonly position: Position;
  readonly linkTarget: Element | null;
};

export const linkUpdate = actionCreator<LinkUpdatePayload>(
  '[navigatorActions] LINK_UPDATE'
);

export type SelectionEnd = {
  readonly nodeId: string;
};
export const selectionEnd = actionCreator<SelectionEnd>(
  '[navigatorActions] SELECTION_END'
);

export type SetLinkSourcePayload = {
  readonly linkSourceNodeId: string | null;
  readonly linkSourceIds: ArdoqId[];
  readonly startPosition: Position;
  readonly isStartInNavigator: boolean;
  readonly refDirection: ReferenceDirection;
  readonly abortController: AbortController;
};

export const setLinkSources = actionCreator<SetLinkSourcePayload>(
  '[navigatorActions] SET_LINK_SOURCE'
);

export type SetLinkTargetPayload = {
  // TODO fix this
  targetId: string | null;
};
export const setLinkTarget = actionCreator<SetLinkTargetPayload>(
  '[navigatorActions] SET_LINK_TARGET'
);
export type TreeChangedPayload = {
  reloadNodeIds?: string[];
  removedIds?: string[];
};
// TODO: This action is not used in the codebase
export const treeChanged = actionCreator<TreeChangedPayload>(
  '[navigatorActions] TREE_CHANGED'
);

export type ShowFilteredComponentsChangedPayload = {
  showFilteredComponents: boolean;
};
export const showFilteredComponentsChanged =
  actionCreator<ShowFilteredComponentsChangedPayload>(
    '[navigatorActions] SHOW_FILTERED_COMPONENTS_CHANGED'
  );

export type IsViewPointModePayload = {
  isViewpointMode: boolean;
};

export const setIsViewpointMode = actionCreator<IsViewPointModePayload>(
  '[navigatorActions] SET_IS_VIEWPOINT_MODE'
);

export type DraggableElementClickedPayload = {
  event: React.MouseEvent<HTMLDivElement, MouseEvent>;
  target?: HTMLElement;
};
export const draggableElementClicked =
  actionCreator<DraggableElementClickedPayload>(
    '[navigatorActions] DRAGGABLE_ELEMENT_CLICKED'
  );

export const clickedInNavigator = actionCreator<React.MouseEvent>(
  '[navigatorActions] CLICKED_IN_NAVIGATOR'
);

export type ExpandCollapseToggleClickedPayload = {
  nodeId: string;
  isDeep?: boolean;
};

export const expandCollapseToggleClicked =
  actionCreator<ExpandCollapseToggleClickedPayload>(
    '[navigatorActions] TOGGLE_EXPAND'
  );

export const contextMenuClicked = actionCreator<MouseEvent>(
  '[navigatorActions] CONTEXT_MENU_CLICKED'
);
export const afterDragStart = actionCreator<{
  ids: Set<string>;
  entityType?: string;
}>('[dragAndDrop] AFTER_DRAG_START');

export const beforeDragEnd = actionCreator<{
  ids: Set<string>;
  targetDropId: string | null;
  dropContext: string | null;
  entityType?: string;
}>('[dragAndDrop] BEFORE_DRAG_END');

export const streamInitialized = actionCreator(
  '[navigatorActions] STREAM_IS_INITIALIZED'
);

export type ExpandNodeInTreePayload = {
  nodeId: string;
};

export const expandNodeInTree = actionCreator<ExpandNodeInTreePayload>(
  '[navigatorActions] EXPAND_NODE_IN_TREE'
);

export const setFilterTerm = actionCreator<string>(
  '[MainAppModuleSidebar] SET_FILTER_TERM'
);

export const componentSelectedInHierarchy = actionCreator<string>(
  '[MainAppModuleSidebar] COMPONENT_SELECTED_IN_HIERARCHY'
);

export const disabledOptionsChanged = actionCreator<string[]>(
  '[navigatorActions] DISABLED_OPTIONS_CHANGED'
);

export const permissionContextChanged = actionCreator<PermissionContext>(
  '[navigatorActions] PERMISSION_CONTEXT_CHANGED'
);

export const selectedComponentIdsOnOpeningDropdown = actionCreator<ArdoqId[]>(
  '[navigatorActions] SELECTED_COMPONENT_IDS_ON_OPENING_DROPDOWN'
);

export const initialComponentsSelectedInTree = actionCreator<ArdoqId[]>(
  '[navigatorActions] INITIAL_COMPONENTS_SELECTED_IN_TREE'
);

export type ApplyInitialTreeConfigurationPayload = {
  filterTerm?: string;
  componentId?: ArdoqId;
};

export const applyInitialTreeConfiguration =
  actionCreator<ApplyInitialTreeConfigurationPayload>(
    '[navigatorActions] APPLY_INITIAL_TREE_CONFIGURATION'
  );
